<template>
  <div class="grid-container text-base">
    <!-- Header -->
    <section-header variants="dividing" size="xl">
      <template slot="content">
        <heading level="1" size="xl" qaid="shipments-inquiry-heading" v-html="bp('heading')" />
      </template>
      <template slot="actions">
        <div class="buttons">
          <trek-link
            button
            secondary
            small
            :href="`${encodedContextPath}/my-account/orders/`"
            qaid="shipments-inquiry-go-to-order-history"
          >
            <span v-html="bp('goToOrderHistory')"></span>
          </trek-link>
        </div>
      </template>
    </section-header>

    <p class="mt-3" qaid="shipments-inquiry-description" v-html="bp('description')" />

    <section-header variants="dividing" size="md" class="mt-3">
      <template slot="content">
        <heading level="1" size="md" qaid="shipments-inquiry-filters-heading" v-html="bp('filter')" />
      </template>
    </section-header>

    <div class="grid-x grid-margin-x">
      <div class="cell large-4">
        <form-group name="shipDate" :label="bp('shipDate')">
          <template v-slot:input>
            <date-picker
              ref="dateRangePicker"
              mode="range"
              :default-date="urlParamDefaultDate"
              :allow-input="true"
              qaid="shipments-inquiry-ship-date-field"
              @change="dateFilterInput"
            />
            <span
              class="underline hover:no-underline cursor-pointer"
              qaid="shipments-inquiry-view-last-thirty-days"
              @click="getLast30Days"
              v-html="bp('view30Days')"
            />
          </template>
        </form-group>
        <form-group
          v-model="options.shipToNumber"
          type="text"
          name="shipToNumber"
          :label="bp('storeNumber')"
          :placeholder="bp('shipToNumberPlaceholder')"
          :maxlength="8"
          qaid="shipments-inquiry-store-number"
          @input="updateShipToValue"
        />
      </div>

      <div class="cell large-4">
        <form-group
          v-model="options.packingSlipNumber"
          type="text"
          name="customerPo"
          :label="bp('packingSlipNumber')"
          :placeholder="bp('enterSlip')"
          :maxlength="8"
          qaid="shipments-inquiry-packing-slip-number"
          @input="updatePackingSlipNumber"
        />
      </div>
      <div class="cell large-4 flex md:justify-end h-4">
        <div class="buttons buttons--right-for-md">
          <trek-button
            id="filter-shipments-button"
            primary
            class="lg:w-full"
            qaid="shipments-inquiry-filter-shipments-button"
            @click="fetchAllOrders"
          >
            <span v-html="bp('filter')"></span>
          </trek-button>
          <trek-button
            id="clear-filters-button"
            secondary
            class="lg:w-full"
            qaid="shipments-inquiry-clear-filter-button"
            @click="clearFilters"
          >
            <span v-html="bp('clearFilters')"></span>
          </trek-button>
        </div>
      </div>
    </div>

    <div>
      <pdl-loading page :is-loading="loading" />

      <!-- Orders Tabs -->
      <section class="my-5">
        <pdl-tabs ref="tabs" :value="currentTab" @input="tabChanged">
          <pdl-tab-pane :key="`${bikesCount}-bike`" name="bike">
            <h3 slot="label" class="heading heading--md m-0">
              <span qaid="shipments-inquiry-bikes-tab-label" v-html="`${bp('bikes')}`" />
              <span qaid="shipments-inquiry-bikes-tab-count"> {{ bikesCount }}</span>
            </h3>
            <shipments-inquiry-grid
              :grid-data="bike"
              :columns="tableHeaders"
              :base-properties="baseProperties"
              :size="bike.pageSize"
            />
          </pdl-tab-pane>
          <pdl-tab-pane :key="`${aftermarketCount}-aftermarket`" name="aftermarket">
            <h3 slot="label" class="heading heading--md m-0">
              <span qaid="shipments-inquiry-accessories-tab-label" v-html="`${bp('accessories')} `" />
              <span qaid="shipments-inquiry-accessories-tab-count"> {{ aftermarketCount }}</span>
            </h3>
            <shipments-inquiry-grid
              :grid-data="aftermarket"
              :columns="aftermarketHeaders"
              :base-properties="baseProperties"
              :size="aftermarket.pageSize"
            />
          </pdl-tab-pane>
        </pdl-tabs>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {mapState} from 'vuex';
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import Heading from '@/components/Heading';
import FormGroup from '@/components/FormGroup';
import DatePicker from '@/components/DatePicker';
import {PdlLoading} from '@pedal/pdl-loading';
import SectionHeader from '@/components/SectionHeader';
import ShipmentsInquiryGrid from '@/components/containers/my-account/ShipmentsInquiryGrid';
import {Url} from '@/utils/url';

const DEFAULT_OPTIONS = {
  endDate: null,
  startDate: null,
  shipToNumber: null,
  packingSlipNumber: null,
};

export default {
  components: {
    TrekButton,
    TrekLink,
    PdlLoading,
    PdlTabs,
    PdlTabPane,
    Heading,
    FormGroup,
    DatePicker,
    SectionHeader,
    ShipmentsInquiryGrid,
  },
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    tableHeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: true,
      bike: {
        type: 'bike',
        items: null,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
      },
      aftermarket: {
        type: 'aftermarket',
        items: null,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
      },
      options: {...DEFAULT_OPTIONS},
      currentTab: 'bike',
    };
  },

  computed: {
    bikesCount() {
      return this.bike.totalItemCount ? `(${this.bike.totalItemCount})` : '(0)';
    },

    aftermarketCount() {
      return this.aftermarket.totalItemCount ? `(${this.aftermarket.totalItemCount})` : '(0)';
    },

    aftermarketHeaders() {
      // Remove the hangtag column from the aftermarket grid because they're not available for them.
      return this.tableHeaders.filter((header) => header.key !== 'hangTag');
    },

    urlParamDefaultDate() {
      let urlParamDate;
      if (this.options.startDate && this.options.endDate) {
        urlParamDate = [new Date(this.options.startDate), new Date(this.options.endDate)];
      } else {
        urlParamDate = null;
      }
      return urlParamDate;
    },

    ...mapState('backend', ['encodedContextPath']),
  },

  created() {
    this.setFiltersFromUrlParams();
    this.fetchAllOrders();
  },

  provide() {
    return {
      container: this,
    };
  },

  methods: {
    async fetchAllOrders() {
      Url.removeUrlParameterObjectFromUrl(this.options);
      Url.appendObjectToUrlParameters(this.options);

      await this.fetchOrders('bike');
      this.fetchOrders('aftermarket');
    },

    fetchOrders(type = 'bike') {
      axios
        .get('/my-account/shipments/', {
          params: {
            productType: type,
            pageIndex: this[type].pageIndex,
            pageSize: this[type].pageSize,
            shipToNumber: this.options.shipToNumber,
            packingSlipNumber: this.options.packingSlipNumber,
            shippingStartDate: this.options.startDate,
            shippingEndDate: this.options.endDate,
          },
        })
        .then((response) => {
          if (response && response.data && response.data.data) {
            let data = response.data.data;

            this[type] = {
              type: type,
              items: data.shipments,
              totalItemCount: data.paging.totalItemCount,
              pageIndex: this[type].pageIndex,
              pageSize: this[type].pageSize,
            };

            if (
              this[type].totalItemCount > 0 &&
              this[type].pageSize > 0 &&
              this[type].totalItemCount / this[type].pageSize > 1
            ) {
              this[type].pageCount = Math.ceil(this[type].totalItemCount / this[type].pageSize);
            } else {
              this[type].pageCount = 0;
            }

            if (this[type].pageIndex > this[type].pageCount) {
              this[type].pageIndex = 0;
              this.fetchOrders(type);
            }

            if (this.options.packingSlipNumber && Array.isArray(data.shipments) && data.shipments?.length > 0) {
              if (data.shipments[0]?.productType === 'Bikes') {
                this.currentTab = 'bike';
              } else {
                this.currentTab = 'aftermarket';
              }
            }
          }

          this.loading = false;
        });
    },

    dateFilterInput({startDate, endDate}) {
      if (startDate) {
        this.options.startDate = window.flatpickr.formatDate(startDate, 'm/d/Y');
      } else {
        this.options.startDate = null;
      }
      if (endDate) {
        this.options.endDate = window.flatpickr.formatDate(endDate, 'm/d/Y');
      } else {
        this.options.endDate = null;
      }
    },

    tabChanged(paneName) {
      this.currentTab = paneName;
    },

    updateShipToValue() {
      const shipToMaxLength = 8;
      this.options.shipToNumber = this.options.shipToNumber.replace(/[^\d]/g, '');
      if (String(this.options.shipToNumber).length > shipToMaxLength) {
        this.options.shipToNumber = this.options.shipToNumber.substring(0, shipToMaxLength);
      }
    },

    updatePackingSlipNumber() {
      const packingSlipNumberMaxLength = 8;
      this.options.packingSlipNumber = this.options.packingSlipNumber.replace(/[^\d]/g, '');
      if (String(this.options.packingSlipNumber).length > packingSlipNumberMaxLength) {
        this.options.packingSlipNumber = this.options.packingSlipNumber.substring(0, packingSlipNumberMaxLength);
      }
    },

    getLast30Days() {
      let date = new Date();
      let endDate = date.toISOString();
      let startDate = new Date(date.setDate(date.getDate() - 30)).toISOString();

      startDate = startDate.substring(0, startDate.indexOf('T'));
      endDate = endDate.substring(0, endDate.indexOf('T'));

      this.options = {};

      this.$refs.dateRangePicker.fp.setDate([startDate, endDate]);
      this.options.startDate = window.flatpickr.formatDate(this.$refs.dateRangePicker.fp.selectedDates[0], 'm/d/Y');
      this.options.endDate = window.flatpickr.formatDate(this.$refs.dateRangePicker.fp.selectedDates[1], 'm/d/Y');

      this.fetchAllOrders();
    },

    clearFilters() {
      Url.removeUrlParameterObjectFromUrl(this.options);
      this.$refs.dateRangePicker.fp.clear();
      this.options = {};
      this.fetchAllOrders();
    },
    setFiltersFromUrlParams() {
      this.urlParams = Url.parse(window.location.search);

      for (const urlParameter in this.urlParams) {
        for (const filterOption in this.options) {
          if (urlParameter === filterOption && this.urlParams[urlParameter] !== null) {
            //url only uses strings, so we need to convert string booleans back to booleans
            if (this.urlParams[urlParameter] === 'true') {
              this.urlParams[urlParameter] = true;
            } else if (this.urlParams[urlParameter] === 'false') {
              this.urlParams[urlParameter] = false;
            }
            this.options[urlParameter] = this.urlParams[urlParameter];
          }
        }
      }
    },
  },
};
</script>
